import { useFieldErrors } from '../../hooks/useFieldErrors';
import { useMemo, useState } from 'react';
import {
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Button, Text, Input, Field, Dropdown, Option, Spinner, tokens,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchMovements, deleteMovement, updateMovement } from '../../api/inventory.api';
import { useT } from '../../i18n';
import { useIntlFormat } from '../../hooks/useIntlFormat';
import { useLocaleSettings } from '../../hooks/useLocaleSettings';
import type { WarehouseMovement } from '../../types/api.types';

interface StockDiagnosticsPanelProps {
  warehouseId: string;
  productId:   string;
  open:        boolean;
  onClose:     () => void;
}

/**
 * Panel de diagnóstico por producto (cola de stock negativo, Task 9): gráfica
 * de evolución del stock + historial de movimientos, editable — salvo los
 * movimientos con recordedBy='system' (auto-deducción del Compliance Guard),
 * que quedan de solo lectura porque no tienen vínculo de vuelta a la
 * operación de campo que los generó.
 */
export function StockDiagnosticsPanel({ warehouseId, productId, open, onClose }: StockDiagnosticsPanelProps) {
  const t  = useT();
  const { fmtNum } = useIntlFormat();
  const qc = useQueryClient();
  const { formatCurrency, formatDate } = useLocaleSettings();
  const [deleteTarget, setDeleteTarget] = useState<WarehouseMovement | null>(null);
  const [editTarget, setEditTarget] = useState<WarehouseMovement | null>(null);
  const [editQty, setEditQty] = useState('');
  const fe = useFieldErrors();
  const [editType, setEditType] = useState<'IN' | 'OUT'>('OUT');

  const { data: allMovements = [], isLoading } = useQuery<WarehouseMovement[]>({
    queryKey: ['movements', warehouseId],
    queryFn:  () => fetchMovements(warehouseId),
    enabled:  open && !!warehouseId,
  });

  const productMovements = useMemo(
    () => allMovements
      .filter(m => m.productId === productId)
      .slice()
      .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)),
    [allMovements, productId],
  );

  const series = useMemo(() => {
    let running = 0;
    return productMovements.map(m => {
      running += m.type === 'ENTRY' ? m.quantity : -m.quantity;
      return { date: formatDate((m.occurredAt ?? '').slice(0, 10)), qty: running };
    });
  }, [productMovements]);

  const deleteMut = useMutation({
    mutationFn: (m: WarehouseMovement) => deleteMovement(warehouseId, m.movementId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movements', warehouseId] });
      qc.invalidateQueries({ queryKey: ['negative-stock-alerts'] });
      qc.invalidateQueries({ queryKey: ['stock', warehouseId] });
      setDeleteTarget(null);
    },
  });

  const editMut = useMutation({
    mutationFn: () => updateMovement(warehouseId, editTarget!.movementId, {
      qty: parseFloat(editQty),
      type: editType,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movements', warehouseId] });
      qc.invalidateQueries({ queryKey: ['negative-stock-alerts'] });
      qc.invalidateQueries({ queryKey: ['stock', warehouseId] });
      setEditTarget(null);
    },
  });

  const openEdit = (m: WarehouseMovement) => {
    setEditTarget(m);
    setEditQty(String(m.quantity));
    setEditType(m.type === 'ENTRY' ? 'IN' : 'OUT');
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface style={{ maxWidth: '760px' }}>
        <DialogBody>
          <DialogTitle>{t.inventory.diagnosticsTitle}</DialogTitle>
          <DialogContent>
            {isLoading ? (
              <Text size={200}>{t.inventory.loadingMovements}</Text>
            ) : (
              <>
                <div data-testid="stock-diagnostics-chart" style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series}>
                      <XAxis dataKey="date" fontSize={11} />
                      <YAxis fontSize={11} />
                      <Tooltip />
                      <Line type="monotone" dataKey="qty" stroke={tokens.colorBrandForeground1} dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {productMovements.length === 0 ? (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.noMovements}</Text>
                ) : (
                  <table style={{ width: '100%', marginTop: '16px', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 0', color: tokens.colorNeutralForeground3, fontSize: '12px' }}>{t.inventory.tableDate}</th>
                        <th style={{ textAlign: 'left', padding: '6px 0', color: tokens.colorNeutralForeground3, fontSize: '12px' }}>{t.inventory.tableType}</th>
                        <th style={{ textAlign: 'right', padding: '6px 0', color: tokens.colorNeutralForeground3, fontSize: '12px' }}>{t.inventory.tableQuantity}</th>
                        <th style={{ textAlign: 'left', padding: '6px 0', color: tokens.colorNeutralForeground3, fontSize: '12px' }}>{t.inventory.tableReason}</th>
                        <th style={{ textAlign: 'right', padding: '6px 0', color: tokens.colorNeutralForeground3, fontSize: '12px' }}>{t.inventory.colActions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productMovements.map((m) => {
                        const isSystem = m.recordedBy === 'system';
                        return (
                          <tr key={m.movementId}>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>{formatDate((m.occurredAt ?? '').slice(0, 10))}</td>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
                              {m.type === 'ENTRY' ? t.inventory.movementTypeIn : t.inventory.movementTypeOut}
                            </td>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, textAlign: 'right', fontWeight: 600 }}>
                              {fmtNum(m.quantity)}
                            </td>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground3 }}>
                              {m.reason}{m.cost != null ? ` · ${formatCurrency(m.cost)}` : ''}
                            </td>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, textAlign: 'right' }}>
                              {isSystem ? (
                                <Text
                                  size={200}
                                  title={t.inventory.systemMovementLockedTooltip}
                                  style={{ color: tokens.colorNeutralForeground3 }}
                                >
                                  {t.inventory.systemMovementLocked}
                                </Text>
                              ) : (
                                <>
                                  <Button
                                    size="small"
                                    appearance="subtle"
                                    onClick={() => openEdit(m)}
                                  >
                                    {t.inventory.editMovement}
                                  </Button>
                                  <Button
                                    size="small"
                                    appearance="subtle"
                                    onClick={() => setDeleteTarget(m)}
                                  >
                                    {t.inventory.deleteMovement}
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {deleteTarget && (
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Text size={200}>{t.inventory.confirmDeleteMovement}</Text>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button
                        appearance="primary"
                        style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
                        onClick={() => deleteMut.mutate(deleteTarget)}
                        disabled={deleteMut.isPending}
                      >
                        {t.common.delete}
                      </Button>
                      <Button appearance="secondary" onClick={() => setDeleteTarget(null)} disabled={deleteMut.isPending}>
                        {t.inventory.cancel}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(_, d) => { if (!d.open) { setEditTarget(null); fe.clear(); } }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.inventory.editMovementDialogTitle}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
                <Field label={t.inventory.fieldMovementType}>
                  <Dropdown
                    value={editType === 'IN' ? t.inventory.movementTypeIn : t.inventory.movementTypeOut}
                    selectedOptions={[editType]}
                    onOptionSelect={(_, d) => setEditType(d.optionValue as 'IN' | 'OUT')}
                    disabled={editMut.isPending}
                  >
                    <Option value="IN">{t.inventory.movementTypeIn}</Option>
                    <Option value="OUT">{t.inventory.movementTypeOut}</Option>
                  </Dropdown>
                </Field>
                <Field label={t.inventory.fieldQty} required {...fe.props('qty')}>
                  <Input
                    type="number"
                    value={editQty}
                    onChange={(_, d) => { setEditQty(d.value); fe.clear('qty'); }}
                    disabled={editMut.isPending}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setEditTarget(null); fe.clear(); }} disabled={editMut.isPending}>
                {t.inventory.cancel}
              </Button>
              <Button
                appearance="primary"
                onClick={() => { if (fe.validate({ qty: (!editQty.trim() || parseFloat(editQty) <= 0) && t.inventory.qtyRequired })) editMut.mutate(); }}
                disabled={editMut.isPending}
                icon={editMut.isPending ? <Spinner size="tiny" /> : undefined}
              >
                {t.common.save}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
